// src/modules/home/components/LandingPage.js

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../../../shared/components/Logo';
import { Navbar } from "../../../shared";
import Footer from '../../../shared/components/layout/Footer';
import SEO from '../../../shared/components/SEO';
import { Trans, useTranslation } from 'react-i18next';
import { Carousel, RecipeCard, LeadMagnetModal, ExitOfferModal, CTAButton } from '../../../shared/components/ui';
import leadService from '../../lead/services/leadService';
import { guestMenuService } from '../../menu/services/guestMenuService';
import authService from '../../auth/services/authService';

const LandingPage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
    const [isExitOfferOpen, setIsExitOfferOpen] = useState(false);
    const autoShowTimerRef = useRef(null);
    const exitIntentTriggeredRef = useRef(false);
    const [demoLoginLoading, setDemoLoginLoading] = useState(false);
    const [demoLoginError, setDemoLoginError] = useState('');

    // Mostrar modal automáticamente después de 30 segundos
    useEffect(() => {
        autoShowTimerRef.current = setTimeout(() => {
            setIsLeadModalOpen(true);
        }, 30000); // 30 segundos

        // Limpiar timer al desmontar el componente
        return () => {
            if (autoShowTimerRef.current) {
                clearTimeout(autoShowTimerRef.current);
            }
        };
    }, []); // Solo se ejecuta una vez al montar

    const handleDemoAccess = async () => {
        if (demoLoginLoading) {
            return;
        }

        setDemoLoginError('');
        setDemoLoginLoading(true);

        try {
            const data = await guestMenuService.login('demo@heaful.com', 'Demo123!');

            const userData = {
                email: data.user?.email || 'demo@heaful.com',
                preferences: data.preferences || null,
                token: data.token || null
            };

            localStorage.setItem('user_data', JSON.stringify(userData));

            if (data.token) {
                authService.setToken(data.token);
            }

            navigate('/plan');
        } catch (error) {
            setDemoLoginError(error?.message || t('landing.demo.error'));
        } finally {
            setDemoLoginLoading(false);
        }
    };

    const handleExitAccept = () => {
        setIsExitOfferOpen(false);
        navigate('/register?promo=EXIT35');
    };

    useEffect(() => {
        const enableExitIntent = typeof window !== 'undefined' && window.innerWidth >= 768;

        if (!enableExitIntent) {
            return;
        }

        const handleMouseLeave = (event) => {
            if (exitIntentTriggeredRef.current || isLeadModalOpen || isExitOfferOpen) {
                return;
            }

            if (event.clientY <= 0 && (!event.relatedTarget || event.relatedTarget.nodeName === 'HTML')) {
                exitIntentTriggeredRef.current = true;

                if (autoShowTimerRef.current) {
                    clearTimeout(autoShowTimerRef.current);
                    autoShowTimerRef.current = null;
                }

                setIsLeadModalOpen(false);
                setIsExitOfferOpen(true);
            }
        };

        document.addEventListener('mouseout', handleMouseLeave);

        return () => {
            document.removeEventListener('mouseout', handleMouseLeave);
        };
    }, [isLeadModalOpen, isExitOfferOpen]);

    const benefits = [
        {
            icon: '⏱️',
            title: t('landing.benefits.saveTime.title'),
            description: t('landing.benefits.saveTime.description')
        },
        {
            icon: '🍃',
            title: t('landing.benefits.saveFood.title'),
            description: t('landing.benefits.saveFood.description')
        },
        {
            icon: '💰',
            title: t('landing.benefits.saveMoney.title'),
            description: t('landing.benefits.saveMoney.description')
        },
        {
            icon: '📚',
            title: t('landing.benefits.allRecipes.title'),
            description: t('landing.benefits.allRecipes.description')
        },
        {
            icon: '🎯',
            title: t('landing.benefits.eatBetter.title'),
            description: t('landing.benefits.eatBetter.description')
        },
        {
            icon: '✏️',
            title: t('landing.benefits.customizeRecipes.title'),
            description: t('landing.benefits.customizeRecipes.description')
        }
    ];

    const steps = [
        {
            number: '1',
            icon: '⚙️',
            title: t('landing.howItWorks.step1'),
            description: t('landing.howItWorks.step1Desc')
        },
        {
            number: '2',
            icon: '✨',
            title: t('landing.howItWorks.step2'),
            description: t('landing.howItWorks.step2Desc')
        },
        {
            number: '3',
            icon: '📋',
            title: t('landing.howItWorks.step3'),
            description: t('landing.howItWorks.step3Desc')
        },
        {
            number: '4',
            icon: '🍽️',
            title: t('landing.howItWorks.step4'),
            description: t('landing.howItWorks.step4Desc')
        }
    ];

    const faqs = [
        {
            question: t('landing.faq.q1.question'),
            answer: t('landing.faq.q1.answer')
        },
        {
            question: t('landing.faq.q2.question'),
            answer: t('landing.faq.q2.answer')
        },
        {
            question: t('landing.faq.q3.question'),
            answer: t('landing.faq.q3.answer')
        },
        /*{
            question: t('landing.faq.q4.question'),
            answer: t('landing.faq.q4.answer')
        }*/
    ];

    // Featured recipes for carousel
    const baseFeaturedRecipes = [
        {
            id: 1,
            key: 'recipe1',
            image: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=800&q=80',
            cookTime: '25 min',
            rating: 4.8
        },
        {
            id: 2,
            key: 'recipe2',
            image: 'https://images.unsplash.com/photo-1455619452474-d2be8b1e70cd?w=800&q=80',
            cookTime: '20 min',
            rating: 4.6
        },
        {
            id: 3,
            key: 'recipe3',
            image: 'https://images.unsplash.com/photo-1574071318508-1cdbab80d002?w=800&q=80',
            cookTime: '35 min',
            rating: 4.9
        },
        {
            id: 4,
            key: 'recipe4',
            image: 'https://images.unsplash.com/photo-1467003909585-2f8a72700288?w=800&q=80',
            cookTime: '30 min',
            rating: 4.7
        },
        {
            id: 5,
            key: 'recipe5',
            image: 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=800&q=80',
            cookTime: '25 min',
            rating: 4.9
        },
        {
            id: 6,
            key: 'recipe6',
            image: 'https://images.unsplash.com/photo-1590301157890-4810ed352733?w=800&q=80',
            cookTime: '10 min',
            rating: 4.5
        }
    ];

    const featuredRecipes = baseFeaturedRecipes.map((recipe) => ({
        ...recipe,
        title: t(`landing.carousel.recipes.${recipe.key}.title`),
        description: t(`landing.carousel.recipes.${recipe.key}.description`),
        difficulty: t(`landing.carousel.recipes.${recipe.key}.difficulty`),
        tags: t(`landing.carousel.recipes.${recipe.key}.tags`, { returnObjects: true })
    }));

    return (

        <div className="min-h-screen relative overflow-hidden bg-gradient-to-b from-primary-50 to-white flex flex-col">
            {/* SEO Meta Tags */}
            <SEO
                title={t('landing.seo.title')}
                description={t('landing.seo.description')}
                url="/"
                image="/SEO/og-landing.jpg"
            />

            {/* decoraciones de fondo sutiles */}
            <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-primary-200 opacity-30 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-teal-200 opacity-30 blur-3xl" />

            <Navbar showAuthLinks={false} />

            {/* Main Content Container */}
            <div className="flex-1 min-h-[80vh]">
                {/* Hero Section */}
                <div className="max-w-6xl mx-auto px-4 py-16 sm:py-20">
                    <div className="text-center">
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900 mb-6">
                            {t('landing.hero.title')}
                        </h1>
                        <p className="text-lg sm:text-xl text-gray-600 mb-10 max-w-3xl mx-auto">
                            {t('landing.hero.subtitle')}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={() => navigate('/pricing')}
                                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-xl font-semibold text-base sm:text-lg transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                            >
                                {t('landing.hero.ctaPrimaryHero')}
                            </button>
                            <button
                                onClick={() => navigate('/login')}
                                className="bg-white hover:bg-gray-50 text-primary-600 border-2 border-primary-600 px-8 py-4 rounded-xl font-semibold text-base sm:text-lg transition-all shadow-sm hover:shadow-md"
                            >
                                {t('landing.hero.ctaSecondary')}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Benefits Section */}
                <div className="max-w-7xl mx-auto px-4 py-16 sm:py-20">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4">
                            <Trans
                                i18nKey="landing.benefits.title"
                                components={{ logo: <Logo inline /> }}
                            />
                        </h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            {t('landing.benefits.subtitle')}
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {benefits.map((benefit, index) => (
                            <div
                                key={index}
                                className="group relative bg-white border-2 border-gray-100 p-8 rounded-3xl hover:border-primary-200 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2"
                            >
                                <div className="absolute -top-6 -left-6 w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center text-3xl shadow-lg transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                                    {benefit.icon}
                                </div>
                                <div className="pt-8">
                                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                                        {benefit.title}
                                    </h3>
                                    <p className="text-gray-600 leading-relaxed">
                                        {benefit.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Featured Recipes Carousel */}
                <div className="bg-white py-16 sm:py-24">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4">
                                {t('landing.carousel.title')}
                            </h2>
                            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
                                {t('landing.carousel.subtitle')}
                            </p>
                            <div className="flex justify-center">
                                <CTAButton
                                    to="/pricing"
                                    variant="primary"
                                >
                                    {t('landing.hero.ctaPrimary')}
                                </CTAButton>
                            </div>
                        </div>
                        <Carousel
                            itemsPerView={3}
                            gap={4}
                            autoPlay={true}
                            autoPlayInterval={60000}
                            speed={40}
                            showDots={true}
                            showArrows={true}
                        >
                            {featuredRecipes.map((recipe) => (
                                <RecipeCard
                                    key={recipe.id}
                                    image={recipe.image}
                                    title={recipe.title}
                                    description={recipe.description}
                                    cookTime={recipe.cookTime}
                                    difficulty={recipe.difficulty}
                                    rating={recipe.rating}
                                    tags={recipe.tags}
                                    onClick={() => navigate('/pricing')}
                                />
                            ))}
                        </Carousel>
                        <Carousel
                            itemsPerView={3}
                            gap={4}
                            autoPlay={true}
                            autoPlayInterval={60000}
                            speed={40}
                            showDots={true}
                            showArrows={true}
                            direction={"right"}
                        >
                            {featuredRecipes.map((recipe) => (
                                <RecipeCard
                                    key={recipe.id}
                                    image={recipe.image}
                                    title={recipe.title}
                                    description={recipe.description}
                                    cookTime={recipe.cookTime}
                                    difficulty={recipe.difficulty}
                                    rating={recipe.rating}
                                    tags={recipe.tags}
                                    onClick={() => navigate('/pricing')}
                                />
                            ))}
                        </Carousel>
                    </div>
                </div>

                {/* Demo Access Section */}
                <div className="px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 lg:pt-24 pb-16 sm:pb-20 lg:pb-24">
                    <div className="relative max-w-5xl mx-auto">
                        <div className="absolute inset-0 rounded-[48px] bg-gradient-to-br from-primary-600 via-primary-500 to-secondary-500 opacity-60 blur-3xl" />
                        <div className="relative bg-white rounded-[36px] shadow-md shadow-primary-300/5 py-24 px-10 sm:px-28 text-center overflow-hidden">
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-secondary-200/40 rounded-full blur-3xl" />
                            <div className="absolute bottom-0 right-0 translate-x-1/3 translate-y-1/3 w-80 h-80 bg-primary-200/30 rounded-full blur-3xl" />
                            <div className="relative space-y-6">
                                <span className="inline-flex items-center gap-2 bg-primary-50 text-primary-700 font-semibold uppercase tracking-[0.3em] text-xs sm:text-sm rounded-full px-6 py-2 shadow-sm shadow-primary-200/60">
                                    {t('landing.demo.eyebrow')}
                                </span>
                                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 leading-tight max-w-3xl mx-auto">
                                    {t('landing.demo.title')}
                                </h2>
                                <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
                                    {t('landing.demo.description')}
                                </p>
                                {demoLoginError && (
                                    <div className="max-w-xl mx-auto">
                                        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-2xl px-4 py-3" role="alert">
                                            {demoLoginError}
                                        </p>
                                    </div>
                                )}
                                <div className="flex flex-col items-center gap-5">
                                    <button
                                        type="button"
                                        onClick={handleDemoAccess}
                                        disabled={demoLoginLoading}
                                        className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-primary-600 via-primary-500 to-secondary-500 hover:from-primary-700 hover:via-primary-600 hover:to-secondary-500 disabled:from-primary-300 disabled:via-primary-300 disabled:to-secondary-300 disabled:cursor-not-allowed text-white px-10 py-4 rounded-full font-semibold text-base sm:text-lg transition-all shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5 disabled:hover:translate-y-0 disabled:hover:shadow-xl"
                                        aria-busy={demoLoginLoading}
                                    >
                                        {demoLoginLoading ? t('landing.demo.ctaLoading') : t('landing.demo.cta')}
                                    </button>
                                    <p className="text-sm sm:text-base text-gray-700">
                                        {t('landing.demo.note')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* How it works */}
                <div className="bg-gradient-to-b from-white to-gray-50 py-24 sm:py-32">
                    <div className="max-w-6xl mx-auto px-4">
                        <div className="text-center mb-12 sm:mb-16">
                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-3 sm:mb-4">
                                {t('landing.howItWorks.title')}
                            </h2>
                            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
                                {t('landing.howItWorks.subtitle')}
                            </p>
                        </div>

                        <div className="relative pl-12 sm:pl-24">
                            <div className="absolute left-6 sm:left-10 top-1 bottom-1 w-1 bg-gradient-to-b from-primary-300 via-primary-400 to-primary-300 rounded-full" />

                            <div className="space-y-14 sm:space-y-20">
                                {steps.map((step, index) => (
                                    <div key={index} className="relative">
                                        <div className="absolute -left-3 sm:-left-6 top-1 flex flex-col items-center">
                                            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-primary-500 to-primary-600 text-white rounded-full flex items-center justify-center text-lg sm:text-2xl font-bold shadow-xl border-4 border-white">
                                                {step.number}
                                            </div>
                                        </div>

                                        <div className="bg-white border border-gray-100 rounded-3xl shadow-md hover:shadow-xl transition-shadow p-8 sm:p-12">
                                            <div className="flex items-start gap-5 sm:gap-6">
                                                <div>
                                                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                                                        {step.title}
                                                    </h3>
                                                    <p className="text-gray-600 leading-relaxed mt-1 sm:mt-2">
                                                        {step.description}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="mt-12 sm:mt-16 text-center">
                            <button
                                onClick={() => navigate('/pricing')}
                                className="inline-flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-xl font-semibold text-base sm:text-lg transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                            >
                                {t('landing.hero.ctaPrimary')}
                            </button>
                        </div>
                    </div>
                </div>

                {/* FAQ */}
                <div className="bg-white py-16 sm:py-20">
                    <div className="max-w-5xl mx-auto px-4">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
                                {t('landing.faq.title')}
                            </h2>
                            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                                {t('landing.faq.subtitle')}
                            </p>
                        </div>

                        <div className="space-y-4">
                            {faqs.map((faq, index) => (
                                <div
                                    key={index}
                                    className="rounded-2xl border border-gray-200 bg-gray-50/60 p-6 sm:p-7 hover:border-primary-200 transition-colors"
                                >
                                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                                        {faq.question}
                                    </h3>
                                    <p className="mt-3 text-gray-600 leading-relaxed">
                                        {faq.answer}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Fun bottom teaser */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-t border-gray-200 py-12">
                    <div className="max-w-4xl mx-auto px-4 text-center">
                        <p className="text-sm uppercase tracking-widest text-gray-500 mb-3">{t('landing.teaser.eyebrow')}</p>
                        <h3 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-4">
                            {t('landing.teaser.title')}
                        </h3>
                        <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                            {t('landing.teaser.subtitle')}
                        </p>
                        <button
                            onClick={() => navigate('/pricing')}
                            className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                        >
                            {t('landing.cta.button')}
                            <span aria-hidden>👉</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="mt-auto">
                <Footer />
            </div>

            {/* Exit Offer Modal */}
            <ExitOfferModal
                isOpen={isExitOfferOpen}
                onClose={() => setIsExitOfferOpen(false)}
                onAccept={handleExitAccept}
            />

            {/* Lead Magnet Modal */}
            <LeadMagnetModal
                isOpen={isLeadModalOpen && !isExitOfferOpen}
                onClose={() => {
                    // Cancelar el timer si el usuario cierra el modal manualmente
                    if (autoShowTimerRef.current) {
                        clearTimeout(autoShowTimerRef.current);
                        autoShowTimerRef.current = null;
                    }
                    setIsLeadModalOpen(false);
                }}
                onSubscribe={async (email) => {
                    return await leadService.subscribeLead(email);
                }}
            />
        </div>
    );
};

export default LandingPage;